import * as Tone from 'tone';

const NOTE_NAMES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];

// Computer keyboard mapping - lower row = C3-B3, upper row = C4-B4
const KEY_MAP_LOWER = {
  z: 'C', x: 'D', c: 'E', v: 'F', b: 'G', n: 'A', m: 'B',
  s: 'C#', d: 'D#', g: 'F#', h: 'G#', j: 'A#',
};
const KEY_MAP_UPPER = {
  q: 'C', w: 'D', e: 'E', r: 'F', t: 'G', y: 'A', u: 'B', i: 'C+',
  2: 'C#', 3: 'D#', 5: 'F#', 6: 'G#', 7: 'A#',
};

export class Keyboard {
  constructor(container, engine) {
    this.container = container;
    this.engine = engine;
    this.baseOctave = 3;
    this.numOctaves = 3;
    this.activeKeys = new Map(); // note -> element
    this.keyboardDown = new Set(); // computer keys currently held

    this._build();
    this._bindComputerKeyboard();
    this._bindMidi();
  }

  _build() {
    const keyboard = document.createElement('div');
    keyboard.className = 'keyboard';

    const whiteNotes = [];
    const blackPositions = [];

    // Build 3 octaves of keys
    let whiteIndex = 0;
    for (let oct = this.baseOctave; oct < this.baseOctave + this.numOctaves; oct++) {
      for (let i = 0; i < 12; i++) {
        const noteName = NOTE_NAMES[i];
        const fullNote = `${noteName}${oct}`;
        const isBlack = noteName.includes('#');

        if (!isBlack) {
          const key = document.createElement('div');
          key.className = 'key key-white';
          key.dataset.note = fullNote;
          keyboard.appendChild(key);

          key.addEventListener('pointerdown', (e) => {
            e.preventDefault();
            this._noteOn(fullNote, key);
            key.setPointerCapture(e.pointerId);
          });
          key.addEventListener('pointerup', () => this._noteOff(fullNote, key));
          key.addEventListener('pointerleave', () => {
            if (this.activeKeys.has(fullNote)) this._noteOff(fullNote, key);
          });

          whiteNotes.push({ el: key, note: fullNote, index: whiteIndex });
          whiteIndex++;
        } else {
          blackPositions.push({ note: fullNote, afterWhite: whiteIndex });
        }
      }
    }

    // Position black keys absolutely over white keys
    const whiteWidth = 36;
    for (const bp of blackPositions) {
      const key = document.createElement('div');
      key.className = 'key key-black';
      key.dataset.note = bp.note;
      key.style.left = `${bp.afterWhite * whiteWidth - 12}px`;
      keyboard.appendChild(key);

      key.addEventListener('pointerdown', (e) => {
        e.preventDefault();
        this._noteOn(bp.note, key);
        key.setPointerCapture(e.pointerId);
      });
      key.addEventListener('pointerup', () => this._noteOff(bp.note, key));
      key.addEventListener('pointerleave', () => {
        if (this.activeKeys.has(bp.note)) this._noteOff(bp.note, key);
      });
    }

    // Set keyboard width
    keyboard.style.width = `${whiteIndex * whiteWidth}px`;

    this.container.appendChild(keyboard);
    this.keyboardEl = keyboard;
  }

  _noteOn(note, el) {
    if (this.activeKeys.has(note)) return;
    this.activeKeys.set(note, el);
    el.classList.add('active');
    this.engine.noteOn(note);
  }

  _noteOff(note, el) {
    this.activeKeys.delete(note);
    if (el) el.classList.remove('active');
    // Also remove active from any key with this note
    const keyEl = this.keyboardEl.querySelector(`[data-note="${note}"]`);
    if (keyEl) keyEl.classList.remove('active');
    this.engine.noteOff(note);
  }

  _bindComputerKeyboard() {
    document.addEventListener('keydown', (e) => {
      if (e.repeat || e.target.tagName === 'SELECT' || e.target.tagName === 'INPUT') return;

      // Octave shift
      if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') {
        this.baseOctave = Math.max(1, this.baseOctave - 1);
        return;
      }
      if (e.key === 'ArrowRight' || e.key === 'ArrowUp') {
        this.baseOctave = Math.min(6, this.baseOctave + 1);
        return;
      }

      const key = e.key.toLowerCase();
      if (this.keyboardDown.has(key)) return;

      let note = null;
      if (KEY_MAP_LOWER[key]) {
        const noteName = KEY_MAP_LOWER[key];
        note = `${noteName}${this.baseOctave}`;
      } else if (KEY_MAP_UPPER[key]) {
        const noteName = KEY_MAP_UPPER[key];
        if (noteName === 'C+') {
          note = `C${this.baseOctave + 2}`;
        } else {
          note = `${noteName}${this.baseOctave + 1}`;
        }
      }

      if (note) {
        this.keyboardDown.add(key);
        const keyEl = this.keyboardEl.querySelector(`[data-note="${note}"]`);
        this._noteOn(note, keyEl || document.createElement('div'));
      }
    });

    document.addEventListener('keyup', (e) => {
      const key = e.key.toLowerCase();
      if (!this.keyboardDown.has(key)) return;
      this.keyboardDown.delete(key);

      let note = null;
      if (KEY_MAP_LOWER[key]) {
        const noteName = KEY_MAP_LOWER[key];
        note = `${noteName}${this.baseOctave}`;
      } else if (KEY_MAP_UPPER[key]) {
        const noteName = KEY_MAP_UPPER[key];
        if (noteName === 'C+') {
          note = `C${this.baseOctave + 2}`;
        } else {
          note = `${noteName}${this.baseOctave + 1}`;
        }
      }

      if (note) {
        const keyEl = this.keyboardEl.querySelector(`[data-note="${note}"]`);
        this._noteOff(note, keyEl);
      }
    });
  }

  _bindMidi() {
    if (!navigator.requestMIDIAccess) return;

    navigator.requestMIDIAccess().then((midi) => {
      for (const input of midi.inputs.values()) {
        input.onmidimessage = (msg) => {
          const [status, noteNum, velocity] = msg.data;
          const command = status & 0xf0;
          const note = Tone.Frequency(noteNum, 'midi').toNote();

          if (command === 0x90 && velocity > 0) {
            const keyEl = this.keyboardEl.querySelector(`[data-note="${note}"]`);
            this._noteOn(note, keyEl || document.createElement('div'));
          } else if (command === 0x80 || (command === 0x90 && velocity === 0)) {
            const keyEl = this.keyboardEl.querySelector(`[data-note="${note}"]`);
            this._noteOff(note, keyEl);
          }
        };
      }
    }).catch(() => {
      // MIDI not available
    });
  }
}
